import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "services/api";
import {
  BanListQuery,
  BannedChannelListItem,
  BannedGroupListItem,
  BannedPeerListItem,
  BannedUserListItem,
  PaginatedResponse,
} from "services/api/banInfo/types";
import {
  BlockedEntity,
  BlockedEntityDetails,
  BlockedEntityTab,
  BlockedFiltersState,
} from "./types";

const DEFAULT_PAGE_SIZE = 20;
const DETAILS_PAGE_SIZE = 8;

const initialFilters: BlockedFiltersState = {
  search: "",
  reason: "",
  dateFrom: "",
  dateTo: "",
  sortBy: "blocked_at",
  sortOrder: "desc",
};

const text = (value: unknown): string => (value == null ? "" : String(value));

const normalizeUsername = (value: unknown): string => {
  const username = text(value).trim();
  if (!username) return "";

  const fromTelegramUrl = username.replace(
    /^https?:\/\/(?:www\.)?t\.me\//i,
    "",
  );
  if (fromTelegramUrl !== username)
    return `@${fromTelegramUrl.replace(/^@/, "")}`;
  return username.startsWith("@") ? username : `@${username}`;
};

const directAvatarUrl = (item: Record<string, unknown>): string => {
  const value = text(
    item.photo_url || item.avatar_url || item.avatar || item.photo,
  ).trim();
  return /^(https?:|blob:|data:)/i.test(value) ? value : "";
};

const avatarHash = (item: Record<string, unknown>): string => {
  const hash = text(item.photo_access_hash).trim();
  return hash && hash !== "0" ? hash : "";
};

const getUserDisplayName = (item: BannedUserListItem): string => {
  const fullName = [text(item.first_name), text(item.last_name)]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (
    fullName ||
    text(item.name) ||
    text(item.username) ||
    `User #${text(item.id)}`
  );
};

const extractApiError = (error: unknown): string => {
  const value = error as any;
  return (
    value?.response?.data?.message ||
    value?.message ||
    "Could not load blocked entities. Please try again."
  );
};

const listSortBy = (
  tab: BlockedEntityTab,
  filters: BlockedFiltersState,
): BanListQuery["sort_by"] => {
  if (filters.sortBy === "created_at") return "created_at";
  if (filters.sortBy === "blocked_at") return "banned_at";
  return tab === "users" ? "username" : "title";
};

const getListReason = (item: Record<string, unknown>): string =>
  text(
    item.restriction_reason ||
      item.blocking_reason ||
      item.delete_reason ||
      item.reason_text,
  ) || "Auto-Moderation restriction";

const isFalseFlag = (value: unknown): boolean =>
  value === false || value === 0 || value === "0" || value === "false";

const getListStatus = (item: Record<string, unknown>): string =>
  isFalseFlag(item.deactivated) ||
  isFalseFlag(item.restricted) ||
  isFalseFlag(item.blocked)
    ? "Review required"
    : "Blocked";

const mapUser = (item: BannedUserListItem): BlockedEntity => ({
  kind: "users",
  id: String(item.id),
  name: getUserDisplayName(item),
  username: normalizeUsername(item.username),
  reason: getListReason(item),
  blockedAt: item.banned_at,
  createdAt: item.created_at,
  status: getListStatus(item),
  avatarUrl: directAvatarUrl(item),
  avatarHash: avatarHash(item),
  raw: item,
});

const mapPeer = (
  kind: "groups" | "channels",
  item: BannedGroupListItem | BannedChannelListItem,
): BlockedEntity => {
  const ownerId =
    item.creator_user_id == null ? "" : String(item.creator_user_id);
  const ownerUsername = normalizeUsername(item.creator_username);

  return {
    kind,
    id: String(item.id),
    name:
      text(item.title) ||
      `${kind === "groups" ? "Group" : "Channel"} #${item.id}`,
    username: normalizeUsername(item.username),
    reason: getListReason(item),
    blockedAt: item.banned_at,
    createdAt: item.created_at,
    status: getListStatus(item),
    owner: ownerUsername || (ownerId ? `User #${ownerId}` : "Not provided"),
    ownerId: ownerId || undefined,
    count: item.participant_count,
    avatarUrl: directAvatarUrl(item),
    avatarHash: avatarHash(item),
    confirmedViolations: item.confirmed_violations,
    raw: item,
  };
};

const buildQuery = (
  tab: BlockedEntityTab,
  filters: BlockedFiltersState,
  search: string,
  page: number,
  pageSize: number,
): BanListQuery => ({
  search: search || undefined,
  reason: filters.reason || undefined,
  date_from: filters.dateFrom || undefined,
  date_to: filters.dateTo || undefined,
  sort_by: listSortBy(tab, filters),
  sort_order: filters.sortOrder,
  page,
  page_size: pageSize,
});

export const useBlockedEntities = (tab: BlockedEntityTab) => {
  const [filters, setFilters] = useState<BlockedFiltersState>(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [items, setItems] = useState<BlockedEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [details, setDetails] = useState<BlockedEntityDetails | null>(null);
  const [logsPage, setLogsPage] = useState(1);
  const [postsPage, setPostsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [reasonOptions, setReasonOptions] = useState<string[]>([]);
  const [reasonsLoading, setReasonsLoading] = useState(true);
  const [reasonsError, setReasonsError] = useState("");
  const requestId = useRef(0);
  const reasonsRequestId = useRef(0);
  const logsRequestId = useRef(0);
  const postsRequestId = useRef(0);

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      350,
    );
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  useEffect(() => {
    const currentRequest = reasonsRequestId.current + 1;
    reasonsRequestId.current = currentRequest;
    setReasonsLoading(true);
    setReasonsError("");

    api.banInfo
      .getReasons(tab)
      .then((response) => {
        if (reasonsRequestId.current !== currentRequest) return;
        const uniqueReasons = Array.from(
          new Set(
            (Array.isArray(response) ? response : [])
              .map(String)
              .filter(Boolean),
          ),
        ).sort((left, right) => left.localeCompare(right));
        setReasonOptions(uniqueReasons);
      })
      .catch((requestError) => {
        if (reasonsRequestId.current !== currentRequest) return;
        setReasonOptions([]);
        setReasonsError(extractApiError(requestError));
      })
      .finally(() => {
        if (reasonsRequestId.current === currentRequest)
          setReasonsLoading(false);
      });
  }, [refreshKey, tab]);

  const loadList = useCallback(async (): Promise<{
    rows: BlockedEntity[];
    total: number;
  }> => {
    const query = buildQuery(tab, filters, debouncedSearch, page, pageSize);

    if (tab === "users") {
      const response = await api.banInfo.getBannedUsers(query);
      const source = Array.isArray(response?.items) ? response.items : [];
      return { rows: source.map(mapUser), total: Number(response?.total || 0) };
    }

    let response: PaginatedResponse<BannedPeerListItem>;
    if (tab === "groups") {
      response = await api.banInfo.getBannedGroups(query);
    } else {
      response = await api.banInfo.getBannedChannels(query);
    }

    const source = Array.isArray(response?.items) ? response.items : [];
    return {
      rows: source.map((item) => mapPeer(tab, item)),
      total: Number(response?.total || 0),
    };
  }, [debouncedSearch, filters, page, pageSize, tab]);

  useEffect(() => {
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    setLoading(true);
    setError("");

    loadList()
      .then(({ rows, total: nextTotal }) => {
        if (requestId.current !== currentRequest) return;
        setItems(rows);
        setTotal(nextTotal);
        setSelectedId((current) => {
          if (current && rows.some((item) => item.id === current))
            return current;
          return rows[0]?.id || "";
        });
      })
      .catch((loadError) => {
        if (requestId.current !== currentRequest) return;
        setItems([]);
        setTotal(0);
        setSelectedId("");
        setError(extractApiError(loadError));
      })
      .finally(() => {
        if (requestId.current === currentRequest) setLoading(false);
      });
  }, [loadList, refreshKey]);

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId],
  );

  useEffect(() => {
    if (!selected) {
      setDetails(null);
      setDetailsLoading(false);
      setDetailsError("");
      return;
    }

    let active = true;
    logsRequestId.current += 1;
    postsRequestId.current += 1;
    setDetails(null);
    setLogsPage(1);
    setPostsPage(1);
    setLogsLoading(false);
    setPostsLoading(false);
    setDetailsLoading(true);
    setDetailsError("");

    const loadDetails = async () => {
      if (selected.kind === "groups") {
        const summary = await api.banInfo.getGroup(selected.id);
        return { summary, logs: [], posts: [], logsTotal: 0, postsTotal: 0 };
      }

      if (selected.kind === "channels") {
        const summary = await api.banInfo.getChannel(selected.id);
        return { summary, logs: [], posts: [], logsTotal: 0, postsTotal: 0 };
      }

      const [summaryResult, logsResult, postsResult] = await Promise.all([
        api.banInfo
          .getUser(selected.id)
          .then((value) => ({ value, error: "" }))
          .catch((requestError) => ({
            value: null,
            error: extractApiError(requestError),
          })),
        api.banInfo
          .getUserLog(selected.id, {
            page: 1,
            page_size: DETAILS_PAGE_SIZE,
            sort_by: "created_at",
            sort_order: "desc",
          })
          .then((value) => ({ value, error: "" }))
          .catch((requestError) => ({
            value: null,
            error: extractApiError(requestError),
          })),
        api.banInfo
          .getUserPosts(selected.id, {
            page: 1,
            page_size: DETAILS_PAGE_SIZE,
            sort_by: "created_at",
            sort_order: "desc",
          })
          .then((value) => ({ value, error: "" }))
          .catch((requestError) => ({
            value: null,
            error: extractApiError(requestError),
          })),
      ]);

      const requestErrors = [
        summaryResult.error,
        logsResult.error,
        postsResult.error,
      ].filter(Boolean);
      if (active && requestErrors.length)
        setDetailsError(Array.from(new Set(requestErrors)).join(" "));

      return {
        summary: summaryResult.value,
        logs: Array.isArray(logsResult.value?.items)
          ? logsResult.value!.items
          : [],
        posts: Array.isArray(postsResult.value?.items)
          ? postsResult.value!.items
          : [],
        logsTotal: Number(logsResult.value?.total || 0),
        postsTotal: Number(postsResult.value?.total || 0),
      };
    };

    loadDetails()
      .then((response) => {
        if (active) setDetails(response);
      })
      .catch((loadError) => {
        if (!active) return;
        setDetails(null);
        setDetailsError(extractApiError(loadError));
      })
      .finally(() => {
        if (active) setDetailsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selected]);

  const changeLogsPage = useCallback(
    async (nextPage: number) => {
      if (
        !selected ||
        selected.kind !== "users" ||
        nextPage < 1 ||
        nextPage === logsPage
      )
        return;

      const previousPage = logsPage;
      const currentRequest = logsRequestId.current + 1;
      logsRequestId.current = currentRequest;
      setLogsPage(nextPage);
      setLogsLoading(true);
      setDetailsError("");

      try {
        const response = await api.banInfo.getUserLog(selected.id, {
          page: nextPage,
          page_size: DETAILS_PAGE_SIZE,
          sort_by: "created_at",
          sort_order: "desc",
        });

        if (logsRequestId.current !== currentRequest) return;
        setDetails((current) =>
          current
            ? {
                ...current,
                logs: Array.isArray(response?.items) ? response.items : [],
                logsTotal: Number(response?.total || 0),
              }
            : current,
        );
      } catch (loadError) {
        if (logsRequestId.current !== currentRequest) return;
        setLogsPage(previousPage);
        setDetailsError(extractApiError(loadError));
      } finally {
        if (logsRequestId.current === currentRequest) setLogsLoading(false);
      }
    },
    [logsPage, selected],
  );

  const changePostsPage = useCallback(
    async (nextPage: number) => {
      if (
        !selected ||
        selected.kind !== "users" ||
        nextPage < 1 ||
        nextPage === postsPage
      )
        return;

      const previousPage = postsPage;
      const currentRequest = postsRequestId.current + 1;
      postsRequestId.current = currentRequest;
      setPostsPage(nextPage);
      setPostsLoading(true);
      setDetailsError("");

      try {
        const response = await api.banInfo.getUserPosts(selected.id, {
          page: nextPage,
          page_size: DETAILS_PAGE_SIZE,
          sort_by: "created_at",
          sort_order: "desc",
        });

        if (postsRequestId.current !== currentRequest) return;
        setDetails((current) =>
          current
            ? {
                ...current,
                posts: Array.isArray(response?.items) ? response.items : [],
                postsTotal: Number(response?.total || 0),
              }
            : current,
        );
      } catch (loadError) {
        if (postsRequestId.current !== currentRequest) return;
        setPostsPage(previousPage);
        setDetailsError(extractApiError(loadError));
      } finally {
        if (postsRequestId.current === currentRequest) setPostsLoading(false);
      }
    },
    [postsPage, selected],
  );

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setPage(1);
  }, []);

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  return {
    filters,
    setFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    items,
    total,
    loading,
    error,
    selected,
    setSelectedId,
    details,
    detailsLoading,
    detailsError,
    detailsPageSize: DETAILS_PAGE_SIZE,
    logsPage,
    postsPage,
    logsLoading,
    postsLoading,
    changeLogsPage,
    changePostsPage,
    reasonOptions,
    reasonsLoading,
    reasonsError,
    resetFilters,
    refresh,
  };
};
